import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box, ToggleButton, ToggleButtonGroup, Button, Popover, CircularProgress,
  Menu, MenuItem, ListItemIcon, Divider, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack,
  RadioGroup, FormControlLabel, Radio, Typography,
} from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import PauseCircleOutlineRoundedIcon from '@mui/icons-material/PauseCircleOutlineRounded';
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import RedoRoundedIcon from '@mui/icons-material/RedoRounded';
import BuildRoundedIcon from '@mui/icons-material/BuildRounded';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import OpacityRoundedIcon from '@mui/icons-material/OpacityRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import LowPriorityRoundedIcon from '@mui/icons-material/LowPriorityRounded';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CallSplitRoundedIcon from '@mui/icons-material/CallSplitRounded';
import MergeRoundedIcon from '@mui/icons-material/MergeRounded';
import { DowntimeWindow } from '../productionTypes';
import { useGanttData } from './useGanttData';
import { analyze, Issue } from './ganttAnalyze';
import { geom, xOf } from './ganttGeometry';
import GanttBoard, { DragState } from './GanttBoard';
import { computeInsertPreview, InsertPreview } from './ganttInsert';
import ReadyToRunDock from './ReadyToRunDock';
import ColourSettingsDrawer from './ColourSettingsDrawer';
import SpeedReviewDialog from './SpeedReviewDialog';
import InkDemandDialog from './InkDemandDialog';
import PressRunSheetDialog from './PressRunSheetDialog';
import HoverPopover, { HoverState } from './HoverPopover';
import AttentionPanel from './AttentionPanel';
import { fmtClock } from './GanttBar';
import { GanttEntry, GanttFamily, GanttSep } from './ganttTypes';
import { ScheduleUpdate } from '../productionApi';
import AcUnitRoundedIcon from '@mui/icons-material/AcUnitRounded';
import { frozenZones } from './ganttFrozenZone';
import {
  GanttView, ATTENTION_CHECKS_ENABLED,
  AUTO_SCROLL_NOW_OFFSET_PX, SNAP_MIN, PARK_CODE, ELSEWHERE_CODE, BACKLOG_CODE, TOKENS,
  ZOOM_DEFAULT_PX, ZOOM_MIN_PX, ZOOM_MAX_PX, ZOOM_WHEEL_SENS, ZOOM_PRESETS,
  INSERT_HYSTERESIS_PX, PLACING_TIMEOUT_MS, InkFilter, inkKey,
  FROZEN_ZONE_ENABLED, FREEZE,
} from './ganttConfig';

/**
 * The drop that has been committed but not yet confirmed by a refetch. Holds
 * the gap open and keeps a solid ghost in the slot, so letting go doesn't snap
 * the board back to the pre-drag layout for the duration of the round trip.
 */
interface Placing { press: string; preview: InsertPreview; jobs: string[]; }

interface Command { undo: () => void; redo: () => void; }

/**
 * A group removal waiting on the planner's acknowledgement. Both kinds take
 * several jobs off the plan at once, so neither happens without the dialog.
 * `held` is a Parking / Scheduled-elsewhere card; `scheduled` is the sibling
 * run of bars sitting on one press lane.
 */
type GroupRemoval =
  | { kind: 'held'; fam: GanttFamily }
  | { kind: 'scheduled'; entries: GanttEntry[] };

// Labels offered when adding a maintenance / approval block.
const BLOCK_CATEGORIES = ['Maintenance', 'Plate change', 'Wash-up', 'Cleaning', 'Customer approval', 'Other'];
// Per-browser memory of the Ready-to-Run dock being expanded over the lanes.
const DOCK_EXPANDED_KEY = 'board.dockExpanded';

interface BlockForm { press: string; startLocal: string; endLocal: string; label: string; }

// Editing a recurring closure (e.g. the weekend band). ISO days 1..7 = Mon..Sun.
const DOW_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
interface RecurringForm {
  id: number; press: string; label: string;
  dowStart: number; todStart: string; dowEnd: number; todEnd: string;
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const toLocalInput = (d: Date) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

export default function PressScheduleGantt() {
  const data = useGanttData();
  const [hourPx, setHourPxState] = useState<number>(ZOOM_DEFAULT_PX);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [hover, setHover] = useState<HoverState | null>(null);
  const [attnAnchor, setAttnAnchor] = useState<HTMLElement | null>(null);
  const [barMenu, setBarMenu] = useState<{ anchor: HTMLElement; entry: GanttEntry } | null>(null);
  const [blockMenu, setBlockMenu] = useState<{ anchor: HTMLElement; window: DowntimeWindow } | null>(null);
  const [blockForm, setBlockForm] = useState<BlockForm | null>(null);
  const [recurringForm, setRecurringForm] = useState<RecurringForm | null>(null);
  const [drag, setDragState] = useState<DragState | null>(null);
  const [placing, setPlacing] = useState<Placing | null>(null);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  // Click-to-schedule on a Ready-to-Run / held family card. The menu offers
  // next-available per press; "specific time" opens the dialog below.
  const [colourDrawerOpen, setColourDrawerOpen] = useState(false);
  const [speedEntry, setSpeedEntry] = useState<GanttEntry | null>(null);
  // Ink colour filter: click a swatch on a Ready-to-Run card to light up every
  // card AND scheduled bar carrying that ink (everything else dims). Clicking
  // the same swatch — or the toolbar chip's × — clears it.
  const [inkFilter, setInkFilter] = useState<InkFilter | null>(null);
  const onInkToggle = useCallback((s: GanttSep) => {
    setInkFilter((prev) => (prev && prev.key === inkKey(s)
      ? null
      : { key: inkKey(s), name: s.name || s.short, hex: s.hex }));
  }, []);
  const [inkEntry, setInkEntry] = useState<GanttEntry | null>(null);
  const [famMenu, setFamMenu] = useState<{ fam: GanttFamily; anchor: HTMLElement } | null>(null);
  const [famSchedule, setFamSchedule] = useState<{ fam: GanttFamily; press: string } | null>(null);
  const [scheduleMode, setScheduleMode] = useState<'next-available' | 'specific-time'>('specific-time');
  const [specificTime, setSpecificTime] = useState<string>('');
  // Admin / testing clean-up: confirm wiping a press's future (not-started) plan.
  const [clearConfirm, setClearConfirm] = useState<string | null>(null);
  // Pending "remove the whole group" — see GroupRemoval.
  const [removeGroupConfirm, setRemoveGroupConfirm] = useState<GroupRemoval | null>(null);
  // Press whose printable Pre-Press run sheet is open.
  const [runSheetPress, setRunSheetPress] = useState<string | null>(null);

  const dragRef = useRef<DragState | null>(null);
  const setDrag = (d: DragState | null) => { dragRef.current = d; setDragState(d); };
  // Set by Esc mid-drag: the pointerup handler then tears down without
  // committing, so an accidental grab costs nothing.
  const dragCancelledRef = useRef(false);
  // The entries array as it stood when the drop was committed. The gap is held
  // open until this reference changes (i.e. a refetch has landed) so the board
  // never flashes back to the pre-drag layout mid-round-trip.
  const entriesAtDropRef = useRef<GanttEntry[] | null>(null);
  // hourPx kept in a ref too so the wheel/pointer listeners read the live zoom.
  const hourPxRef = useRef(hourPx);
  const setHourPx = (px: number) => { hourPxRef.current = px; setHourPxState(px); };
  // Canvas horizon, in a ref so the stable wheel/pointer callbacks read it live.
  const windowMinRef = useRef(data.windowMin);
  windowMinRef.current = data.windowMin;
  const scrollRef = useRef<HTMLDivElement>(null);

  // Latest data/entries via refs so the stable pointer + undo callbacks below
  // never close over a stale render.
  const dataRef = useRef(data);
  dataRef.current = data;
  const entriesRef = useRef(data.entries);
  entriesRef.current = data.entries;
  // Live selection for the keyboard handlers (avoids re-binding the listener).
  const selectedIdRef = useRef(selectedId);
  selectedIdRef.current = selectedId;

  // ── undo / redo command stack (single-user → no conflict handling) ────────
  const undoStack = useRef<Command[]>([]);
  const redoStack = useRef<Command[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const syncHist = useCallback(() => {
    setCanUndo(undoStack.current.length > 0);
    setCanRedo(redoStack.current.length > 0);
  }, []);
  const pushCmd = useCallback((c: Command) => {
    undoStack.current.push(c);
    redoStack.current = [];
    syncHist();
  }, [syncHist]);
  const undo = useCallback(() => {
    const c = undoStack.current.pop();
    if (c) { c.undo(); redoStack.current.push(c); syncHist(); }
  }, [syncHist]);
  const redo = useCallback(() => {
    const c = redoStack.current.pop();
    if (c) { c.redo(); undoStack.current.push(c); syncHist(); }
  }, [syncHist]);

  // Non-destructive PATCH that records its inverse for undo.
  const patchWithUndo = useCallback((id: number, patch: ScheduleUpdate, prev: ScheduleUpdate) => {
    dataRef.current.patchEntry.mutate({ id, patch });
    pushCmd({
      undo: () => dataRef.current.patchEntry.mutate({ id, patch: prev }),
      redo: () => dataRef.current.patchEntry.mutate({ id, patch }),
    });
  }, [pushCmd]);

  // Unschedule a bar; undo re-creates it on its old press at its old start.
  // (The re-added row gets a fresh id, so redo of a removal is a no-op in v1.)
  const removeWithUndo = useCallback((entry: GanttEntry) => {
    const startIso = new Date(dataRef.current.baseMs + entry.absStart * 60000).toISOString();
    dataRef.current.removeEntry.mutate({ id: entry.id });
    pushCmd({
      undo: () => dataRef.current.addEntry.mutate({
        job_number: entry.job, press_code: entry.press, planned_start: startIso,
      }),
      redo: () => {},
    });
  }, [pushCmd]);

  // The sibling bars a Ready-to-Run card was scheduled as. Since migration 141
  // a bar that landed on a press carries its FAMILY RUN id, and that is the
  // group: two runs of one forme on the same lane (a split) stay two groups,
  // and a rejoined pair is one. Bars with no run yet (rows adopted from press
  // actuals) fall back to customer|forme, which is also how held chips group.
  // Deliberately press-scoped — if a run's bars were dragged over two presses
  // one at a time, removing the group on P15 leaves P16 alone. Only
  // not-yet-started bars qualify: a running or finished job is never pulled
  // off the plan.
  const sameRun = (a: GanttEntry, b: GanttEntry): boolean => (
    (a.familyRunId != null || b.familyRunId != null)
      ? a.familyRunId === b.familyRunId
      : a.cust === b.cust && (a.forme ?? null) === (b.forme ?? null)
  );
  const groupOf = useCallback((entry: GanttEntry): GanttEntry[] => entriesRef.current
    .filter((e) => e.press === entry.press && e.status === 'planned' && sameRun(e, entry))
    .sort((a, b) => a.sequence - b.sequence), []);
  // A split-off bar can rejoin when its forme has ANOTHER planned run on the
  // same lane and pass — the run it was split from, or its siblings' run.
  const canRejoin = useCallback((entry: GanttEntry): boolean => entry.familyRunId != null
    && entriesRef.current.some((e) => e.press === entry.press
      && e.status === 'planned'
      && e.pass === entry.pass
      && e.familyRunId != null
      && e.familyRunId !== entry.familyRunId
      && e.cust === entry.cust
      && (e.forme ?? null) === (entry.forme ?? null)), []);

  // Unschedule a whole group of bars; a single undo puts every job back on its
  // old press at its old start. (Re-added rows get fresh ids, so redo is a no-op
  // — same limitation as the single-bar removal above.)
  const removeGroupWithUndo = useCallback((entries: GanttEntry[]) => {
    if (entries.length === 0) return;
    const snapshot = entries.map((e) => ({
      job_number: e.job,
      press_code: e.press,
      planned_start: new Date(dataRef.current.baseMs + e.absStart * 60000).toISOString(),
    }));
    dataRef.current.removeEntries.mutate({ ids: entries.map((e) => e.id) });
    pushCmd({
      undo: () => { snapshot.forEach((s) => dataRef.current.addEntry.mutate(s)); },
      redo: () => {},
    });
  }, [pushCmd]);

  // Act on the acknowledged group removal. Held cards just lose their source
  // rows (returnToBacklog); scheduled runs go through the undoable path. Either
  // way the jobs have no schedule entry left, so Ready-to-Run picks them back up.
  const confirmGroupRemoval = useCallback(() => {
    if (!removeGroupConfirm) return;
    if (removeGroupConfirm.kind === 'held') {
      dataRef.current.returnToBacklog.mutate({ fam: removeGroupConfirm.fam });
    } else {
      removeGroupWithUndo(removeGroupConfirm.entries);
    }
    setRemoveGroupConfirm(null);
  }, [removeGroupConfirm, removeGroupWithUndo]);

  // ── maintenance / approval blocks (one-off closed downtime windows) ───────
  const openBlockDialog = useCallback(() => {
    const firstPress = dataRef.current.lanes.find((l) => l.kind === 'press')?.code ?? 'P15';
    const now = new Date();
    now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0); // snap to next 15 min
    const end = new Date(now.getTime() + 120 * 60000); // default 2h window
    setBlockForm({ press: firstPress, startLocal: toLocalInput(now), endLocal: toLocalInput(end), label: 'Maintenance' });
  }, []);

  const submitBlock = useCallback(async (form: BlockForm) => {
    setBlockForm(null);
    const start = new Date(form.startLocal);
    const end = new Date(form.endLocal);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    if (end.getTime() <= start.getTime()) return; // end must be after start
    try {
      const win = await dataRef.current.createBlock.mutateAsync({
        press_code: form.press, kind: 'one_off', effect: 'closed',
        starts_at: start.toISOString(), ends_at: end.toISOString(), label: form.label || 'Block',
      });
      pushCmd({ undo: () => dataRef.current.deleteBlock.mutate({ id: win.id }), redo: () => {} });
    } catch {
      /* surfaced by the mutation; nothing to roll back */
    }
  }, [pushCmd]);

  const removeBlockWithUndo = useCallback((w: DowntimeWindow) => {
    dataRef.current.deleteBlock.mutate({ id: w.id });
    // Recreate with the SAME kind on undo (recurring closures and one-off
    // blocks carry different fields).
    pushCmd({
      undo: () => dataRef.current.createBlock.mutate({
        press_code: w.press_code, kind: w.kind, effect: w.effect,
        starts_at: w.starts_at, ends_at: w.ends_at,
        dow_start: w.dow_start, tod_start: w.tod_start,
        dow_end: w.dow_end, tod_end: w.tod_end, label: w.label,
      }),
      redo: () => {},
    });
  }, [pushCmd]);

  const onBlockClick = useCallback((w: DowntimeWindow, el: HTMLElement) => {
    setBlockMenu({ anchor: el, window: w });
  }, []);

  // Edit a recurring closure's days/times (e.g. adjust the weekend band as
  // rostering hours change). Applies to EVERY week — it's the standing pattern.
  const openRecurringEdit = useCallback((w: DowntimeWindow) => {
    setRecurringForm({
      id: w.id, press: w.press_code, label: w.label ?? 'Closed',
      dowStart: w.dow_start ?? 5, todStart: (w.tod_start ?? '14:00').slice(0, 5),
      dowEnd: w.dow_end ?? 1, todEnd: (w.tod_end ?? '06:00').slice(0, 5),
    });
  }, []);

  const submitRecurring = useCallback(async (form: RecurringForm) => {
    setRecurringForm(null);
    try {
      await dataRef.current.updateBlock.mutateAsync({
        id: form.id,
        patch: {
          dow_start: form.dowStart, tod_start: form.todStart,
          dow_end: form.dowEnd, tod_end: form.todEnd,
        },
      });
    } catch {
      /* surfaced by the mutation */
    }
  }, []);

  // Set a backlog family aside as "scheduled elsewhere" (held on the ELSEWHERE
  // sentinel press — leaves the Ready-to-Run list but is restorable).
  const onScheduleElsewhere = useCallback((fam: GanttFamily) => {
    dataRef.current.scheduleFamily.mutate({ fam, press: ELSEWHERE_CODE, planned_start: null });
  }, []);
  // Return a held (elsewhere/parking) family to the Ready-to-Run backlog.
  const onReturnToBacklog = useCallback((fam: GanttFamily) => {
    dataRef.current.returnToBacklog.mutate({ fam });
  }, []);

  // ── click-to-schedule a family (the menu-driven alternative to drag-drop) ──
  // Open the schedule menu anchored to the clicked card.
  const onScheduleFamilyMenu = useCallback((fam: GanttFamily, anchor: HTMLElement) => {
    setHover(null);
    setFamMenu({ fam, anchor });
  }, []);
  // Append the whole family at a press's tail (null start → backend appends).
  const scheduleFamilyNextAvailable = useCallback((fam: GanttFamily, press: string) => {
    dataRef.current.scheduleFamily.mutate({ fam, press, planned_start: null });
  }, []);
  // Open the specific-time dialog, defaulted to the first press and the next
  // quarter-hour.
  const openSpecificTimeDialog = useCallback((fam: GanttFamily) => {
    const firstPress = dataRef.current.lanes.find((l) => l.kind === 'press')?.code ?? 'P15';
    const now = new Date();
    now.setMinutes(Math.ceil(now.getMinutes() / 15) * 15, 0, 0);
    setScheduleMode('specific-time');
    setSpecificTime(toLocalInput(now));
    setFamSchedule({ fam, press: firstPress });
  }, []);
  const submitFamSchedule = useCallback(() => {
    if (!famSchedule) return;
    const { fam, press } = famSchedule;
    if (scheduleMode === 'next-available') {
      dataRef.current.scheduleFamily.mutate({ fam, press, planned_start: null });
    } else {
      const d = new Date(specificTime);
      if (Number.isNaN(d.getTime())) return;
      dataRef.current.scheduleFamily.mutate({ fam, press, planned_start: d.toISOString(), at_anchor: true });
    }
    setFamSchedule(null);
  }, [famSchedule, scheduleMode, specificTime]);
  // Split a ticked subset of a family into its own Ready-to-Run card; rejoin merges back.
  // On a scheduled bar the same calls split its family run / merge it back.
  const onSplitJobs = useCallback((jobNumbers: string[]) => {
    dataRef.current.splitFamily.mutate({ jobNumbers });
  }, []);
  const onRejoinSplit = useCallback((splitKey: string) => {
    dataRef.current.rejoinFamily.mutate({ splitKey });
  }, []);
  const onRejoinJobs = useCallback((jobNumbers: string[]) => {
    dataRef.current.rejoinFamily.mutate({ jobNumbers });
  }, []);

  // Target sequence for a drop: count siblings whose midpoint precedes the
  // drop X (the schedule is sequence-packed, so position == order).
  const seqFromDrop = useCallback((press: string, excludeId: number, dropAbs: number | null): number | null => {
    if (dropAbs == null) return null;
    const sibs = entriesRef.current
      .filter((e) => e.press === press && e.id !== excludeId)
      .sort((a, b) => a.sequence - b.sequence);
    let idx = 0;
    for (const s of sibs) {
      if (dropAbs >= s.absStart + s.planned_total / 2) idx += 1;
      else break;
    }
    return idx + 1;
  }, []);

  const analysis = useMemo(() => analyze(data.entries as any), [data.entries]);
  const attnCount = analysis.issues.length + analysis.counts.overbooked;

  // Scroll so NOW sits ~260px from the left, at a given zoom.
  const scrollNowIntoView = useCallback((px: number) => {
    const target = Math.max(0, xOf(data.nowAbs, geom(px, windowMinRef.current)) - AUTO_SCROLL_NOW_OFFSET_PX);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollLeft = target;
    }));
  }, [data.nowAbs]);

  // Step the timeline horizontally by N days (week arrows), at the current zoom.
  const scrollByDays = useCallback((days: number) => {
    const el = scrollRef.current;
    if (el) el.scrollBy({ left: days * 24 * hourPxRef.current, behavior: 'smooth' });
  }, []);

  // Once the board mounts (loaded), centre NOW. Not re-run on wheel zoom —
  // zoom-to-cursor manages its own scroll so the view stays under the pointer.
  useEffect(() => {
    if (!data.isLoading) scrollNowIntoView(hourPxRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.isLoading]);

  // Continuous mouse-wheel zoom, anchored to the pointer. Attached as a
  // non-passive listener so we can preventDefault the page scroll. Re-runs
  // when loading flips so it binds once the scroll element exists.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onWheel = (ev: WheelEvent) => {
      ev.preventDefault();
      const rect = el.getBoundingClientRect();
      const offsetX = ev.clientX - rect.left;            // px from viewport-left of timeline
      const px = hourPxRef.current;
      const g = geom(px, windowMinRef.current);
      const timeAtCursor = g.anchor + ((offsetX + el.scrollLeft) / g.hourPx) * 60;
      const factor = Math.exp(-ev.deltaY * ZOOM_WHEEL_SENS);
      const next = Math.min(ZOOM_MAX_PX, Math.max(ZOOM_MIN_PX, px * factor));
      if (next === px) return;
      const ng = geom(next, windowMinRef.current);
      const newScrollLeft = ((timeAtCursor - ng.anchor) / 60) * ng.hourPx - offsetX;
      setHourPx(next);
      // Double-rAF so the new (wider/narrower) content has laid out before we
      // re-anchor, otherwise the browser clamps scrollLeft on zoom-in.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (scrollRef.current) scrollRef.current.scrollLeft = Math.max(0, newScrollLeft);
      }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.isLoading]);

  const onHover = useCallback((e: GanttEntry, ev: React.MouseEvent) => {
    if (dragRef.current) return;
    setHover({ entry: e, x: ev.clientX, y: ev.clientY });
  }, []);
  const onLeave = useCallback(() => setHover(null), []);

  // Click a scheduled bar → action menu (move between presses / park / unschedule).
  // Only planned jobs can be moved; in-progress/completed stay put.
  const onBarMenu = useCallback((entry: GanttEntry, el: HTMLElement) => {
    if (entry.status !== 'planned') return;
    setHover(null);
    setBarMenu({ anchor: el, entry });
  }, []);
  // Context-menu actions — all non-destructive PATCH (or undoable remove).
  const doMove = useCallback((toPress: string) => {
    setBarMenu((m) => {
      if (m) {
        const entry = m.entry;
        if (toPress === BACKLOG_CODE) {
          removeWithUndo(entry);
        } else {
          patchWithUndo(
            entry.id,
            { press_code: toPress },
            { press_code: entry.press, sequence: entry.sequence },
          );
        }
      }
      return null;
    });
  }, [patchWithUndo, removeWithUndo]);

  // Swap a scheduled bar one slot earlier (-1) / later (+1) in its press
  // sequence. The board is sequence-packed, so this is what "reorder" means;
  // the server re-cascades the times. Undoable. Returns whether it moved
  // (so the keyboard handler knows the press was meaningful).
  const reorderEntry = useCallback((entry: GanttEntry, dir: -1 | 1): boolean => {
    if (entry.status !== 'planned') return false;
    const sibs = entriesRef.current
      .filter((e) => e.press === entry.press)
      .sort((a, b) => a.sequence - b.sequence);
    const idx = sibs.findIndex((e) => e.id === entry.id);
    const tgt = idx + dir;
    if (idx < 0 || tgt < 0 || tgt >= sibs.length) return false; // already at the end
    patchWithUndo(entry.id, { sequence: sibs[tgt].sequence }, { sequence: entry.sequence });
    return true;
  }, [patchWithUndo]);

  // Reorder to an explicit sequence — the run sheet's drag-and-drop, which can
  // move a job several slots at once rather than one step. Same PATCH and same
  // undo entry as the board's ←/→, so a drag on the sheet and a keypress here
  // are one history.
  //
  // SERIALISED. Each PATCH resolves its shift against the row's CURRENT server
  // sequence, so two of them in flight at once can be applied in either order
  // and leave a queue nobody asked for. A planner reordering a run sheet drags
  // several rows in a few seconds — well inside one round trip — so the writes
  // are chained rather than raced. (The undo entry still snapshots the sequence
  // as last rendered, so undoing a move made before its own refetch landed can
  // restore a stale slot — the same single-user limitation the ←/→ path has.)
  const reorderChainRef = useRef<Promise<unknown>>(Promise.resolve());
  const queuePatch = useCallback((id: number, patch: ScheduleUpdate) => {
    const run = () => dataRef.current.patchEntry.mutateAsync({ id, patch });
    // A failed PATCH must not poison the chain (or raise an unhandled
    // rejection) — the mutation reports it, and the run sheet's optimistic
    // order falls back to the server's on its own timeout.
    reorderChainRef.current = reorderChainRef.current.then(run, run).catch(() => {});
  }, []);
  const reorderEntryTo = useCallback((entry: GanttEntry, toSequence: number) => {
    if (entry.status !== 'planned') return;
    const prevSeq = entry.sequence;
    queuePatch(entry.id, { sequence: toSequence });
    pushCmd({
      undo: () => queuePatch(entry.id, { sequence: prevSeq }),
      redo: () => queuePatch(entry.id, { sequence: toSequence }),
    });
  }, [queuePatch, pushCmd]);

  // Reorder from the action menu (keeps the menu open so you can step repeatedly).
  const doReorder = useCallback((dir: -1 | 1) => {
    setBarMenu((m) => { if (m) reorderEntry(m.entry, dir); return m; });
  }, [reorderEntry]);

  // Keyboard: undo/redo, plus ←/→ to reorder the selected bar in its queue.
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      const tag = (ev.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      const meta = ev.metaKey || ev.ctrlKey;
      // Esc mid-drag abandons the move — the gap closes and nothing is written.
      if (ev.key === 'Escape' && dragRef.current) {
        ev.preventDefault();
        dragCancelledRef.current = true;
        setDrag({ ...dragRef.current, overLane: null, dropAbs: null, preview: null });
        return;
      }
      if (meta && (ev.key === 'z' || ev.key === 'Z')) {
        ev.preventDefault();
        if (ev.shiftKey) redo(); else undo();
      } else if (meta && (ev.key === 'y' || ev.key === 'Y')) {
        ev.preventDefault();
        redo();
      } else if (!meta && (ev.key === 'ArrowLeft' || ev.key === 'ArrowRight')) {
        const id = selectedIdRef.current;
        if (id == null) return; // no bar selected → let arrows do their normal thing
        const entry = entriesRef.current.find((e) => e.id === id);
        if (!entry) return;
        ev.preventDefault();
        setBarMenu(null); // close the action menu if it was open; keep selection
        reorderEntry(entry, ev.key === 'ArrowLeft' ? -1 : 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, reorderEntry]);

  const pressLanes = data.lanes.filter((l) => l.kind === 'press');

  // Is the menu's entry already first / last in its press queue? (disables the
  // reorder items at the ends).
  const reorderEdge = (entry: GanttEntry) => {
    const sibs = data.entries.filter((e) => e.press === entry.press).sort((a, b) => a.sequence - b.sequence);
    const idx = sibs.findIndex((e) => e.id === entry.id);
    return { first: idx <= 0, last: idx === sibs.length - 1 };
  };

  const jumpToIssue = useCallback((iss: Issue) => {
    const px = iss.day === 0 ? ZOOM_PRESETS.day : ZOOM_PRESETS['3day'];
    setHourPx(px);
    setSelectedId(Number(iss.id));
    setAttnAnchor(null);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollLeft = Math.max(0, xOf(iss.absStart, geom(px, windowMinRef.current)) - 320);
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── pointer-event drag (matches the prototype; a Gantt drop needs a
  //    continuous time position, which dnd-kit's droppable-zone model doesn't
  //    give us cleanly) ────────────────────────────────────────────────────
  const onPointerMove = useCallback((ev: PointerEvent) => {
    const cur = dragRef.current;
    if (!cur) return;
    setCursor({ x: ev.clientX, y: ev.clientY });
    const el = document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null;
    const track = el?.closest('[data-lane]') as HTMLElement | null;
    if (!track) { setDrag({ ...cur, overLane: null, dropAbs: null, preview: null }); return; }
    const lane = track.dataset.lane as string;
    const kind = track.dataset.laneKind;
    const g = geom(hourPxRef.current, windowMinRef.current);
    const rect = track.getBoundingClientRect();
    let abs = g.anchor + ((ev.clientX - rect.left) / g.hourPx) * 60;
    abs = Math.round(abs / SNAP_MIN) * SNAP_MIN;
    abs = Math.max(g.anchor, Math.min(abs, g.anchor + g.window - 60));
    // Resolve the drop to a real queue position here (not at render time), so
    // the ghost the planner sees and the payload we POST/PATCH are the same
    // decision. Duration comes from the plan parts, not planned_total, which is
    // a view span that already includes a downtime pause at the OLD position.
    const d = dataRef.current;
    const preview = kind === 'park' ? null : computeInsertPreview({
      laneEntries: entriesRef.current.filter((e) => e.press === lane),
      movingId: cur.entry?.id ?? null,
      durationMin: cur.entry
        ? cur.entry.setupMin + cur.entry.runMin + cur.entry.washupMin
        : (cur.tile!.setup + cur.tile!.run + cur.tile!.washup),
      cursorAbs: abs,
      // Only the TARGET lane's closures shape the gap — same per-press rule the
      // bars themselves follow in adaptEntry. Unfiltered, a drop onto one press
      // would size its gap around another press's downtime.
      windows: d.downtimeWindows.filter((w) => w.press_code === lane),
      baseMs: d.baseMs,
      nowAbs: d.nowAbs,
      // Only carry the previous slot forward within the same lane — crossing to
      // another press is a fresh decision, not a wobble.
      prevIdx: cur.overLane === lane ? cur.preview?.idx ?? null : null,
      hysteresisMin: (INSERT_HYSTERESIS_PX / hourPxRef.current) * 60,
    });
    setDrag({
      tile: cur.tile, entry: cur.entry ?? null, overLane: lane,
      dropAbs: kind === 'park' ? null : abs, preview,
    });
  }, []);

  const onPointerUp = useCallback(() => {
    window.removeEventListener('pointermove', onPointerMove);
    const d = dragRef.current;
    const cancelled = dragCancelledRef.current;
    dragCancelledRef.current = false;
    // A parked family dropped back onto Parking is a no-op (already held).
    const reparkingSame = d?.tile?.parked && d.overLane === PARK_CODE;
    if (!cancelled && d?.overLane && d.tile && !reparkingSame) {
      const press = d.overLane;
      // Drop onto a press → insert at the previewed slot. Parking has no queue.
      if (press !== PARK_CODE && d.preview) {
        // Hold the gap open until the new bars actually arrive.
        entriesAtDropRef.current = entriesRef.current;
        setPlacing({ press, preview: d.preview, jobs: d.tile.jobs.map((j) => j.job) });
        data.scheduleFamily.mutate({
          fam: d.tile, press,
          planned_start: new Date(data.base.getTime() + d.preview.startAbs * 60000).toISOString(),
          at_sequence: d.preview.seq,
        });
      } else {
        data.scheduleFamily.mutate({ fam: d.tile, press, planned_start: null });
      }
    }
    setDrag(null);
    setCursor(null);
  }, [onPointerMove, data.base, data.scheduleFamily]);

  // Ready-to-Run dock expanded over the lanes. Remembered per browser: the
  // planner who lives in the pool view gets it back on reload, a shared floor
  // screen keeps whatever it was last set to. Storage can be unavailable
  // (private window, blocked site data) — fall back to docked.
  //
  // HOW IT MOVES. Nothing here animates a layout property. The first version
  // animated the lanes' max-height, which re-laid-out every bar on the board
  // on every frame and stuttered. Now the dock is lifted OUT of the flow for
  // the duration of the move (position: absolute over the whole body, full
  // height) and slid with a transform only — the compositor does that on its
  // own thread, so it stays smooth however many cards or bars are on screen.
  // The lanes never change size; they are simply covered. Layout changes
  // exactly once, when the dock is handed back to the flow after sliding home.
  //
  //   dockExpanded  the target: pool over the lanes (persisted)
  //   dockOverlay   the dock is out of the flow — while expanded AND while
  //                 sliding home; false only once it is docked and still
  const [dockExpanded, setDockExpanded] = useState<boolean>(() => {
    try { return localStorage.getItem(DOCK_EXPANDED_KEY) === '1'; } catch { return false; }
  });
  const [dockOverlay, setDockOverlay] = useState<boolean>(dockExpanded);
  const dockExpandedRef = useRef(dockExpanded);
  dockExpandedRef.current = dockExpanded;
  const lanesRef = useRef<HTMLDivElement>(null);
  const dockRef = useRef<HTMLDivElement>(null);
  const dockAnimRef = useRef<Animation | null>(null);
  const dockMountedRef = useRef(false);
  const setDock = useCallback((v: boolean) => {
    if (v === dockExpandedRef.current) return;
    dockAnimRef.current?.cancel();
    dockAnimRef.current = null;
    try { localStorage.setItem(DOCK_EXPANDED_KEY, v ? '1' : '0'); } catch { /* per-browser convenience only */ }
    if (v) setDockOverlay(true);      // out of the flow first, then the slide up (layout effect)
    setDockExpanded(v);               // re-docking keeps the overlay until the slide home finishes
  }, []);
  const toggleDock = useCallback(() => setDock(!dockExpandedRef.current), [setDock]);

  // The slide itself. Runs after React has put the dock in the overlay (so
  // it is already full height) and before the browser paints, so the first
  // frame is already at the start of the motion. "Home" is the lanes' bottom
  // edge — the dock's top when docked — measured live, so it is right at any
  // window size. Reduced motion: no slide, just the two end states.
  useLayoutEffect(() => {
    const dock = dockRef.current;
    const lanes = lanesRef.current;
    if (!dock || !lanes || !dockOverlay) return;
    const firstRun = !dockMountedRef.current;
    dockMountedRef.current = true;
    const reduce = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const home = lanes.offsetHeight;
    if (dockExpanded) {
      if (firstRun || reduce) return;                       // a fresh load doesn't slide
      dockAnimRef.current = dock.animate(
        [{ transform: `translateY(${home}px)` }, { transform: 'translateY(0)' }],
        { duration: 520, easing: 'cubic-bezier(.22,.9,.24,1)', fill: 'backwards' },
      );
      dockAnimRef.current.onfinish = () => { dockAnimRef.current = null; };
    } else {
      if (reduce) { setDockOverlay(false); return; }
      const a = dock.animate(
        [{ transform: 'translateY(0)' }, { transform: `translateY(${home}px)` }],
        { duration: 460, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' },
      );
      dockAnimRef.current = a;
      a.onfinish = () => {
        // Hold the final frame inline, drop the animation, then hand the dock
        // back to the flow. The inline transform is cleared in the layout
        // effect below, before the browser paints — no flash of either state.
        dock.style.transform = `translateY(${home}px)`;
        a.cancel();
        dockAnimRef.current = null;
        setDockOverlay(false);
      };
    }
  }, [dockExpanded, dockOverlay]);
  useLayoutEffect(() => {
    if (!dockOverlay && dockRef.current) dockRef.current.style.transform = '';
  }, [dockOverlay]);

  const onGrabFamily = useCallback((fam: GanttFamily, ev: React.PointerEvent) => {
    ev.preventDefault();
    setHover(null);
    dragCancelledRef.current = false;
    // The lanes are the drop target: grabbing a card while the pool is
    // expanded re-docks it so the lanes slide back in under the cursor.
    if (dockExpandedRef.current) setDock(false);
    setDrag({ tile: fam, overLane: null, dropAbs: null, preview: null });
    setCursor({ x: ev.clientX, y: ev.clientY });
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp, { once: true });
  }, [onPointerMove, onPointerUp, setDock]);

  // ── moving an already-placed bar (reorder / cross-press / hold) ───────────
  // Commit a bar move from the SAME preview the planner was looking at, so the
  // gap they saw open is the slot the job takes. seqFromDrop stays as the
  // fallback for the (rare) case where no preview resolved.
  const commitEntryMove = useCallback((
    entry: GanttEntry, dest: string, dropAbs: number | null, preview: InsertPreview | null,
  ) => {
    if (dest === PARK_CODE) {
      patchWithUndo(entry.id, { press_code: PARK_CODE }, { press_code: entry.press, sequence: entry.sequence });
      return;
    }
    const newSeq = preview?.seq ?? seqFromDrop(dest, entry.id, dropAbs);
    if (dest === entry.press) {
      // in-lane reorder: position maps to sequence
      if (newSeq != null && newSeq !== entry.sequence) {
        patchWithUndo(entry.id, { sequence: newSeq }, { sequence: entry.sequence });
      }
    } else {
      // cross-press: relocate + slot into the previewed position
      patchWithUndo(
        entry.id,
        newSeq != null ? { press_code: dest, sequence: newSeq } : { press_code: dest },
        { press_code: entry.press, sequence: entry.sequence },
      );
    }
  }, [patchWithUndo, seqFromDrop]);

  const onEntryPointerUp = useCallback(() => {
    window.removeEventListener('pointermove', onPointerMove);
    const d = dragRef.current;
    const cancelled = dragCancelledRef.current;
    dragCancelledRef.current = false;
    if (!cancelled && d?.entry && d.overLane) {
      if (d.overLane !== PARK_CODE && d.preview) {
        entriesAtDropRef.current = entriesRef.current;
        setPlacing({ press: d.overLane, preview: d.preview, jobs: [d.entry.job] });
      }
      commitEntryMove(d.entry, d.overLane, d.dropAbs, d.preview ?? null);
    }
    setDrag(null);
    setCursor(null);
  }, [onPointerMove, commitEntryMove]);

  // Hand-off from GanttBar once a press-and-move passes the drag threshold.
  const onBarGrab = useCallback((entry: GanttEntry, ev: React.PointerEvent) => {
    setHover(null);
    setSelectedId(entry.id);
    dragCancelledRef.current = false;
    setDrag({ tile: null, entry, overLane: null, dropAbs: null, preview: null });
    setCursor({ x: ev.clientX, y: ev.clientY });
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onEntryPointerUp, { once: true });
  }, [onPointerMove, onEntryPointerUp]);

  useEffect(() => () => window.removeEventListener('pointermove', onPointerMove), [onPointerMove]);

  // Release the held gap once the move is really on the board: the mutation has
  // settled AND a refetch has replaced the entries we captured at drop time.
  // Both conditions matter — settling alone happens before the refetch lands,
  // and a reference change alone could be the background poll firing mid-flight.
  // The timeout is the escape hatch for a failed mutation, whose error surfaces
  // through the mutation itself.
  const placingPending = data.scheduleFamily.isPending || data.patchEntry.isPending;
  useEffect(() => {
    if (!placing) return undefined;
    if (!placingPending && data.entries !== entriesAtDropRef.current) {
      setPlacing(null);
      return undefined;
    }
    const t = setTimeout(() => setPlacing(null), PLACING_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [placing, placingPending, data.entries]);

  const pressCount = data.lanes.filter((l) => l.kind === 'press').length;
  const todayLabel = data.base.toLocaleDateString('en-NZ', { weekday: 'long', day: 'numeric', month: 'long' });
  // Customers currently visible anywhere on the board — feeds the colour editor
  // so unmapped customers can be given a colour without leaving the board.
  const customerCodes = Array.from(new Set<string>([
    ...data.entries.map((e) => e.cust),
    ...data.families.map((f) => f.cust ?? ''),
    ...data.elsewhereFamilies.map((f) => f.cust ?? ''),
    ...data.parkedFamilies.map((f) => f.cust ?? ''),
  ].filter(Boolean)));
  // Highlight a preset button only when the zoom exactly matches it (wheel
  // zoom lands on custom values → no preset highlighted).
  const activePreset = (Object.keys(ZOOM_PRESETS) as GanttView[]).find((v) => ZOOM_PRESETS[v] === hourPx) ?? null;
  const setPreset = (v: GanttView) => { const px = ZOOM_PRESETS[v]; setHourPx(px); scrollNowIntoView(px); };
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
  const undoHint = isMac ? '⌘Z' : 'Ctrl+Z';
  const redoHint = isMac ? '⌘⇧Z' : 'Ctrl+Shift+Z';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, overflow: 'hidden', background: TOKENS.bg }}>
      {/* toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 1.5, borderBottom: `1px solid ${TOKENS.line}`, background: TOKENS.panel, flexShrink: 0 }}>
        <Box>
          <Box sx={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.02em', color: TOKENS.ink, lineHeight: 1.1 }}>Press Schedule</Box>
          <Box sx={{ fontSize: 12, color: TOKENS.ink3 }}>{pressCount} presses · {todayLabel}</Box>
        </Box>
        <Box sx={{ flex: 1 }} />
        {/* date-horizon navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title="Back one week" arrow>
            <IconButton size="small" onClick={() => scrollByDays(-7)} sx={{ color: TOKENS.ink2 }}>
              <ChevronLeftRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Jump to now" arrow>
            <Button
              size="small" onClick={() => scrollNowIntoView(hourPxRef.current)}
              sx={{ textTransform: 'none', fontSize: 12.5, fontWeight: 600, minWidth: 0, px: 1, color: TOKENS.ink2, borderRadius: '8px' }}
            >
              Today
            </Button>
          </Tooltip>
          <Tooltip title="Forward one week" arrow>
            <IconButton size="small" onClick={() => scrollByDays(7)} sx={{ color: TOKENS.ink2 }}>
              <ChevronRightRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Tooltip title="Scroll over the timeline to zoom" arrow>
          <ToggleButtonGroup
            size="small" exclusive value={activePreset}
            onChange={(_e, v) => { if (v) setPreset(v as GanttView); }}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 12.5, px: 1.5, py: 0.5, borderRadius: '8px' } }}
          >
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="3day">3-day</ToggleButton>
            <ToggleButton value="week">Week</ToggleButton>
          </ToggleButtonGroup>
        </Tooltip>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          <Tooltip title={`Undo (${undoHint})`} arrow>
            <span>
              <IconButton size="small" onClick={undo} disabled={!canUndo}
                sx={{ color: TOKENS.ink2, '&.Mui-disabled': { color: TOKENS.line } }}>
                <UndoRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={`Redo (${redoHint})`} arrow>
            <span>
              <IconButton size="small" onClick={redo} disabled={!canRedo}
                sx={{ color: TOKENS.ink2, '&.Mui-disabled': { color: TOKENS.line } }}>
                <RedoRoundedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Tooltip title="Add a maintenance / approval block" arrow>
          <Button
            onClick={openBlockDialog}
            startIcon={<BuildRoundedIcon sx={{ fontSize: 16 }} />}
            sx={{
              textTransform: 'none', fontSize: 12.5, fontWeight: 600, borderRadius: '8px', px: 1.25,
              color: TOKENS.ink2, border: `1px solid ${TOKENS.line}`,
              '&:hover': { borderColor: `${TOKENS.accent}66`, background: `${TOKENS.accent}0a` },
            }}
          >
            Block
          </Button>
        </Tooltip>
        <Tooltip title="Edit customer bar colours" arrow>
          <IconButton
            onClick={() => setColourDrawerOpen(true)}
            size="small"
            aria-label="Edit customer colours"
            sx={{
              color: TOKENS.ink2, border: `1px solid ${TOKENS.line}`, borderRadius: '8px',
              '&:hover': { borderColor: `${TOKENS.accent}66`, background: `${TOKENS.accent}0a` },
            }}
          >
            <PaletteOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Button
          onClick={(e) => setAttnAnchor(e.currentTarget)}
          startIcon={!ATTENTION_CHECKS_ENABLED ? <FlagOutlinedIcon />
            : attnCount > 0 ? <WarningAmberRoundedIcon /> : <CheckCircleRoundedIcon />}
          sx={{
            textTransform: 'none', fontSize: 12.5, fontWeight: 600, borderRadius: 99, px: 1.5,
            // Checks off = neutral pill: an "All clear" green would claim a
            // verdict nothing actually reached.
            ...(!ATTENTION_CHECKS_ENABLED ? {
              color: TOKENS.ink3, background: 'transparent', border: `1px solid ${TOKENS.line}`,
              '&:hover': { background: TOKENS.bg },
            } : {
              color: attnCount > 0 ? TOKENS.bad : TOKENS.good,
              background: attnCount > 0 ? 'rgba(220,38,38,0.08)' : 'rgba(22,163,74,0.08)',
              border: `1px solid ${attnCount > 0 ? 'rgba(220,38,38,0.3)' : 'rgba(22,163,74,0.3)'}`,
              '&:hover': { background: attnCount > 0 ? 'rgba(220,38,38,0.14)' : 'rgba(22,163,74,0.14)' },
            }),
          }}
        >
          {!ATTENTION_CHECKS_ENABLED ? 'Needs attention'
            : attnCount > 0 ? `${attnCount} need attention` : 'All clear'}
        </Button>
        {FROZEN_ZONE_ENABLED && (() => {
          // The frozen-zone chip: how far ahead the schedule is committed, and
          // when that rolls. Companion to the on-canvas overlay in GanttBoard.
          const fz = frozenZones(data.nowAbs);
          const wd = (absMin: number) => {
            const d = new Date(data.base);
            d.setDate(d.getDate() + Math.floor(absMin / 1440));
            return d.toLocaleDateString('en-NZ', { weekday: 'short' });
          };
          return (
            <Tooltip
              arrow
              title={'Prep runs one day ahead: at 06:00 each morning, ink kitting and CtP plate imaging kick off '
                + 'for the production day starting 24 hours later. The schedule is frozen through '
                + `${wd(fz.freezeAbs)} 06:00; the horizon rolls forward a day at 06:00 tomorrow. `
                + '(Visual only for now — the kitting and plate drivers are not wired up yet.)'}
            >
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.6, px: 1.5, py: 0.5, borderRadius: 99, cursor: 'help',
                border: `1px solid ${FREEZE.ink}44`, background: `${FREEZE.ink}0d`,
                color: FREEZE.ink, fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap',
              }}>
                <AcUnitRoundedIcon sx={{ fontSize: 14 }} />
                Frozen → {wd(fz.freezeAbs)} 06:00
              </Box>
            </Tooltip>
          );
        })()}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5, borderRadius: 99, border: `1px solid ${TOKENS.line}` }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: TOKENS.accent, boxShadow: `0 0 6px ${TOKENS.accent}` }} />
          <Box sx={{ fontFamily: TOKENS.monoFont, fontSize: 13, fontWeight: 600, color: TOKENS.ink, fontVariantNumeric: 'tabular-nums' }}>{fmtClock(data.nowAbs)}</Box>
        </Box>
      </Box>

      {/* body: Gantt sizes to its lanes; Ready-to-Run fills the rest below.
          With the pool expanded the dock sits OVER the lanes (see the dock
          state above); the lanes themselves never change size, and everything
          stays mounted, so nothing refetches and a drag in flight keeps its
          state. */}
      <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        <Box ref={lanesRef} sx={{ flex: '0 1 auto', display: 'flex', minHeight: 0, minWidth: 0, maxHeight: '70%' }}>
          {data.isLoading ? (
            <Box sx={{ flex: 1, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>
          ) : (
            <GanttBoard
              hourPx={hourPx} windowMin={data.windowMin} base={data.base} nowAbs={data.nowAbs}
              entries={data.entries} lanes={data.lanes} downtime={data.downtime}
              parkedCount={data.parkedCount} parkedFamilies={data.parkedFamilies}
              selectedId={selectedId} analysis={analysis}
              drag={drag} placing={placing} onSelect={setSelectedId} onHover={onHover} onLeave={onLeave}
              onGrabFamily={onGrabFamily} onParkedChipClick={onScheduleFamilyMenu} onBarMenu={onBarMenu}
              onBarGrab={onBarGrab} onBlockClick={onBlockClick}
              inkFilter={inkFilter}
              onClearFuture={(press) => setClearConfirm(press)}
              onPrintRunSheet={(press) => setRunSheetPress(press)}
              scrollRef={scrollRef}
            />
          )}
        </Box>
        {/* In the flow when docked; lifted over the whole body (full height,
            transform-slid) while expanded or sliding home. */}
        <Box ref={dockRef} sx={dockOverlay ? {
          // Above everything the Gantt stacks (its ruler, NOW line and drag
          // ghost go up to zIndex 10) — otherwise they bleed through the pool.
          position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 20,
          display: 'flex', flexDirection: 'column', minHeight: 0,
          background: TOKENS.bg, willChange: 'transform',
          boxShadow: '0 -14px 36px rgba(0,0,0,.16)',
        } : {
          flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
        }}>
          <ReadyToRunDock
            families={data.families} totalJobs={data.totalJobs}
            elsewhereFamilies={data.elsewhereFamilies} elsewhereCount={data.elsewhereCount}
            onGrabFamily={onGrabFamily} onScheduleFamily={onScheduleFamilyMenu}
            onScheduleElsewhere={onScheduleElsewhere}
            onReturnToBacklog={onReturnToBacklog} onSplitJobs={onSplitJobs} onRejoinSplit={onRejoinSplit}
            draggingFamId={drag?.tile?.famId ?? null}
            inkFilter={inkFilter} onInkToggle={onInkToggle}
            poolExpanded={dockExpanded} onTogglePool={toggleDock}
          />
        </Box>
      </Box>

      {/* customer colour editor */}
      <ColourSettingsDrawer
        open={colourDrawerOpen}
        onClose={() => setColourDrawerOpen(false)}
        customerCodes={customerCodes}
      />

      {/* inline speed-assumption review for a scheduled bar */}
      <SpeedReviewDialog
        entry={speedEntry}
        open={!!speedEntry}
        onClose={() => setSpeedEntry(null)}
      />

      {/* printable Pre-Press run sheet for one press lane */}
      <PressRunSheetDialog
        open={!!runSheetPress}
        onClose={() => setRunSheetPress(null)}
        lane={data.lanes.find((l) => l.code === runSheetPress) ?? null}
        entries={data.entries}
        base={data.base}
        windows={data.downtimeWindows}
        nowAbs={data.nowAbs}
        onReorder={reorderEntryTo}
      />

      {/* per-separation ink-demand report for a scheduled bar */}
      <InkDemandDialog
        entry={inkEntry}
        open={!!inkEntry}
        onClose={() => setInkEntry(null)}
      />

      {/* attention popover */}
      <Popover
        open={!!attnAnchor} anchorEl={attnAnchor} onClose={() => setAttnAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: '12px', boxShadow: TOKENS.shadowMd, mt: 1 } } }}
      >
        <AttentionPanel analysis={analysis} onJump={jumpToIssue} />
      </Popover>

      {/* scheduled-bar action menu: move between presses, park, or unschedule */}
      <Menu
        open={!!barMenu} anchorEl={barMenu?.anchor ?? null} onClose={() => setBarMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: TOKENS.shadowMd, minWidth: 196, mt: 0.5 } } }}
      >
        {barMenu && (
          <Box sx={{ px: 2, pt: 1, pb: 0.5, fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 12.5, color: TOKENS.ink }}>
            {barMenu.entry.job}
            <Box component="span" sx={{ ml: 0.75, fontFamily: 'inherit', fontWeight: 400, color: TOKENS.ink3 }}>on {barMenu.entry.press}</Box>
          </Box>
        )}
        {barMenu && (
          <MenuItem onClick={() => { setSpeedEntry(barMenu.entry); setBarMenu(null); }}>
            <ListItemIcon><SpeedRoundedIcon fontSize="small" /></ListItemIcon>
            Review speed…
          </MenuItem>
        )}
        {barMenu && (
          <MenuItem onClick={() => { setInkEntry(barMenu.entry); setBarMenu(null); }}>
            <ListItemIcon><OpacityRoundedIcon fontSize="small" /></ListItemIcon>
            Ink demand…
          </MenuItem>
        )}
        {barMenu && <Divider sx={{ my: 0.5 }} />}
        {barMenu && (
          <MenuItem disabled={reorderEdge(barMenu.entry).first} onClick={() => doReorder(-1)}>
            <ListItemIcon><ArrowBackRoundedIcon fontSize="small" /></ListItemIcon>
            Move earlier
            <Box component="span" sx={{ ml: 'auto', pl: 2, fontFamily: TOKENS.monoFont, fontSize: 12, color: TOKENS.ink3 }}>←</Box>
          </MenuItem>
        )}
        {barMenu && (
          <MenuItem disabled={reorderEdge(barMenu.entry).last} onClick={() => doReorder(1)}>
            <ListItemIcon><ArrowForwardRoundedIcon fontSize="small" /></ListItemIcon>
            Move later
            <Box component="span" sx={{ ml: 'auto', pl: 2, fontFamily: TOKENS.monoFont, fontSize: 12, color: TOKENS.ink3 }}>→</Box>
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        {pressLanes
          .filter((l) => l.code !== barMenu?.entry.press)
          .map((l) => (
            <MenuItem key={l.code} onClick={() => doMove(l.code)}>
              <ListItemIcon><SwapHorizRoundedIcon fontSize="small" /></ListItemIcon>
              Move to {l.code}
            </MenuItem>
          ))}
        <MenuItem onClick={() => doMove(PARK_CODE)}>
          <ListItemIcon><PauseCircleOutlineRoundedIcon fontSize="small" /></ListItemIcon>
          Hold in Parking
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => doMove(BACKLOG_CODE)} sx={{ color: TOKENS.bad }}>
          <ListItemIcon><RemoveCircleOutlineRoundedIcon fontSize="small" sx={{ color: TOKENS.bad }} /></ListItemIcon>
          Remove from schedule
        </MenuItem>
        {/* …and the whole sibling run it was scheduled as (confirmed first) */}
        {barMenu && groupOf(barMenu.entry).length > 1 && (
          <MenuItem
            sx={{ color: TOKENS.bad }}
            onClick={() => setBarMenu((m) => {
              if (m) setRemoveGroupConfirm({ kind: 'scheduled', entries: groupOf(m.entry) });
              return null;
            })}
          >
            <ListItemIcon><DeleteSweepRoundedIcon fontSize="small" sx={{ color: TOKENS.bad }} /></ListItemIcon>
            Remove group from schedule
            <Box component="span" sx={{ ml: 'auto', pl: 2, fontFamily: TOKENS.monoFont, fontSize: 12, color: TOKENS.ink3 }}>
              {groupOf(barMenu.entry).length}
            </Box>
          </MenuItem>
        )}
        {/* family-run split / rejoin — the explicit events the ink room keys off */}
        {barMenu && (groupOf(barMenu.entry).length > 1 || canRejoin(barMenu.entry)) && <Divider sx={{ my: 0.5 }} />}
        {barMenu && groupOf(barMenu.entry).length > 1 && (
          <MenuItem onClick={() => setBarMenu((m) => { if (m) onSplitJobs([m.entry.job]); return null; })}>
            <ListItemIcon><CallSplitRoundedIcon fontSize="small" /></ListItemIcon>
            Split from group
            {barMenu.entry.familyRunId != null && (
              <Box component="span" sx={{ ml: 'auto', pl: 2, fontFamily: TOKENS.monoFont, fontSize: 12, color: TOKENS.ink3 }}>
                run #{barMenu.entry.familyRunId}
              </Box>
            )}
          </MenuItem>
        )}
        {barMenu && canRejoin(barMenu.entry) && (
          <MenuItem onClick={() => setBarMenu((m) => { if (m) onRejoinJobs([m.entry.job]); return null; })}>
            <ListItemIcon><MergeRoundedIcon fontSize="small" /></ListItemIcon>
            Rejoin group
            <Box component="span" sx={{ ml: 'auto', pl: 2, fontFamily: TOKENS.monoFont, fontSize: 12, color: TOKENS.ink3 }}>
              run #{barMenu.entry.familyRunId}
            </Box>
          </MenuItem>
        )}
      </Menu>

      {/* maintenance-block menu (one-off downtime windows) */}
      <Menu
        open={!!blockMenu} anchorEl={blockMenu?.anchor ?? null} onClose={() => setBlockMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: TOKENS.shadowMd, minWidth: 200, mt: 0.5 } } }}
      >
        {blockMenu && (
          <Box sx={{ px: 2, pt: 1, pb: 0.5, fontWeight: 700, fontSize: 12.5, color: TOKENS.ink }}>
            {blockMenu.window.label ?? 'Block'}
            <Box component="span" sx={{ ml: 0.75, fontWeight: 400, color: TOKENS.ink3 }}>
              on {blockMenu.window.press_code}{blockMenu.window.kind === 'recurring' ? ' · weekly' : ''}
            </Box>
          </Box>
        )}
        {blockMenu?.window.kind === 'recurring' && (
          <MenuItem onClick={() => setBlockMenu((m) => { if (m) openRecurringEdit(m.window); return null; })}>
            <ListItemIcon><EditRoundedIcon fontSize="small" /></ListItemIcon>
            Edit hours
          </MenuItem>
        )}
        <MenuItem
          sx={{ color: TOKENS.bad }}
          onClick={() => setBlockMenu((m) => { if (m) removeBlockWithUndo(m.window); return null; })}
        >
          <ListItemIcon><DeleteOutlineRoundedIcon fontSize="small" sx={{ color: TOKENS.bad }} /></ListItemIcon>
          {blockMenu?.window.kind === 'recurring' ? 'Delete closure' : 'Delete block'}
        </MenuItem>
      </Menu>

      {/* family schedule menu — click a Ready-to-Run card to pick a press + timing */}
      <Menu
        open={!!famMenu} anchorEl={famMenu?.anchor ?? null} onClose={() => setFamMenu(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { borderRadius: '10px', boxShadow: TOKENS.shadowMd, minWidth: 248, mt: 0.5 } } }}
      >
        {famMenu && (
          <Box sx={{ px: 2, pt: 1, pb: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Box sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 13, color: TOKENS.ink }}>{famMenu.fam.forme ?? '—'}</Box>
              <Box sx={{ fontSize: 12, fontWeight: 600, color: TOKENS.ink2 }}>{famMenu.fam.cust}</Box>
            </Box>
            <Box sx={{ fontSize: 11.5, color: TOKENS.ink3, mt: 0.25 }}>
              {famMenu.fam.jobs.length} job{famMenu.fam.jobs.length === 1 ? '' : 's'} ·{' '}
              {famMenu.fam.jobs.reduce((s, j) => s + j.sheets, 0).toLocaleString()} sheets
            </Box>
          </Box>
        )}
        <Divider sx={{ my: 0.5 }} />
        {pressLanes.map((l) => (
          <MenuItem
            key={l.code}
            onClick={() => setFamMenu((m) => { if (m) scheduleFamilyNextAvailable(m.fam, l.code); return null; })}
          >
            <ListItemIcon><PlayArrowRoundedIcon fontSize="small" /></ListItemIcon>
            Schedule on {l.code} — next available
          </MenuItem>
        ))}
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => setFamMenu((m) => { if (m) openSpecificTimeDialog(m.fam); return null; })}>
          <ListItemIcon><AccessTimeRoundedIcon fontSize="small" /></ListItemIcon>
          Schedule at specific time…
        </MenuItem>
        <MenuItem onClick={() => setFamMenu((m) => { if (m) onScheduleElsewhere(m.fam); return null; })}>
          <ListItemIcon><LowPriorityRoundedIcon fontSize="small" /></ListItemIcon>
          Schedule elsewhere
        </MenuItem>
        {/* Held cards (Parking / elsewhere) own schedule rows, so they can be
            handed back to Ready-to-Run. Backlog cards are already there. */}
        {famMenu?.fam.jobs.some((j) => j.scheduleId != null) && <Divider sx={{ my: 0.5 }} />}
        {famMenu?.fam.jobs.some((j) => j.scheduleId != null) && (
          <MenuItem
            sx={{ color: TOKENS.bad }}
            onClick={() => setFamMenu((m) => {
              if (m) setRemoveGroupConfirm({ kind: 'held', fam: m.fam });
              return null;
            })}
          >
            <ListItemIcon><DeleteSweepRoundedIcon fontSize="small" sx={{ color: TOKENS.bad }} /></ListItemIcon>
            Remove group from schedule
            <Box component="span" sx={{ ml: 'auto', pl: 2, fontFamily: TOKENS.monoFont, fontSize: 12, color: TOKENS.ink3 }}>
              {famMenu.fam.jobs.length}
            </Box>
          </MenuItem>
        )}
      </Menu>

      {/* specific-time dialog — choose a press + anchor time for the family */}
      <Dialog open={!!famSchedule} onClose={() => setFamSchedule(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
          {famSchedule && (
            <>
              Schedule {famSchedule.fam.forme ?? '—'} · {famSchedule.fam.cust}
              <Typography sx={{ fontSize: 12, color: TOKENS.ink3, fontWeight: 400, mt: 0.25 }}>
                {famSchedule.fam.jobs.length} job{famSchedule.fam.jobs.length === 1 ? '' : 's'} ·{' '}
                {famSchedule.fam.jobs.reduce((s, j) => s + j.sheets, 0).toLocaleString()} sheets
              </Typography>
            </>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: TOKENS.ink2, mb: 0.75 }}>Press</Typography>
            <ToggleButtonGroup
              size="small" exclusive value={famSchedule?.press ?? ''}
              onChange={(_e, val) => { if (val) setFamSchedule((p) => (p ? { ...p, press: val } : p)); }}
            >
              {pressLanes.map((l) => (
                <ToggleButton
                  key={l.code} value={l.code}
                  sx={{
                    px: 2, textTransform: 'none', fontWeight: 600,
                    '&.Mui-selected': { bgcolor: `${TOKENS.accent}1f`, color: TOKENS.accent, borderColor: TOKENS.accent },
                    '&.Mui-selected:hover': { bgcolor: `${TOKENS.accent}2b` },
                  }}
                >
                  {l.code}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <RadioGroup value={scheduleMode} onChange={(e) => setScheduleMode(e.target.value as 'next-available' | 'specific-time')}>
            <FormControlLabel
              value="next-available" control={<Radio />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Next available</Typography>
                  <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>Append after the last job already on this press.</Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="specific-time" control={<Radio />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Specific time</Typography>
                  <Typography sx={{ fontSize: 12, color: TOKENS.ink3 }}>Anchor at the chosen time; existing jobs shuffle behind it.</Typography>
                </Box>
              }
            />
          </RadioGroup>
          {scheduleMode === 'specific-time' && (
            <Box sx={{ mt: 1.5, pl: 4 }}>
              <TextField
                size="small" type="datetime-local" value={specificTime}
                onChange={(e) => setSpecificTime(e.target.value)} fullWidth InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFamSchedule(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" disableElevation onClick={submitFamSchedule}
            sx={{ textTransform: 'none', bgcolor: TOKENS.accent, '&:hover': { bgcolor: '#b25313' } }}
          >
            Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* add maintenance / approval block */}
      <Dialog open={!!blockForm} onClose={() => setBlockForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Add block</DialogTitle>
        {blockForm && (
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                select size="small" label="Press" value={blockForm.press}
                onChange={(e) => setBlockForm({ ...blockForm, press: e.target.value })}
              >
                {pressLanes.map((l) => <MenuItem key={l.code} value={l.code}>{l.code} · {l.name}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Type" value={blockForm.label}
                onChange={(e) => setBlockForm({ ...blockForm, label: e.target.value })}
              >
                {BLOCK_CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
              <TextField
                size="small" type="datetime-local" label="Start" InputLabelProps={{ shrink: true }}
                value={blockForm.startLocal}
                onChange={(e) => setBlockForm({ ...blockForm, startLocal: e.target.value })}
              />
              <TextField
                size="small" type="datetime-local" label="End" InputLabelProps={{ shrink: true }}
                value={blockForm.endLocal}
                onChange={(e) => setBlockForm({ ...blockForm, endLocal: e.target.value })}
                error={!!blockForm.endLocal && new Date(blockForm.endLocal) <= new Date(blockForm.startLocal)}
                helperText={
                  !!blockForm.endLocal && new Date(blockForm.endLocal) <= new Date(blockForm.startLocal)
                    ? 'End must be after start' : ' '
                }
              />
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setBlockForm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" disableElevation sx={{ textTransform: 'none' }}
            onClick={() => { if (blockForm) submitBlock(blockForm); }}
          >
            Add block
          </Button>
        </DialogActions>
      </Dialog>

      {/* edit a recurring closure (e.g. the weekend / plant-closed band) */}
      <Dialog open={!!recurringForm} onClose={() => setRecurringForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
          Edit closure{recurringForm ? ` · ${recurringForm.press}` : ''}
        </DialogTitle>
        {recurringForm && (
          <DialogContent>
            <Box sx={{ fontSize: 12.5, color: TOKENS.ink3, mb: 2 }}>
              “{recurringForm.label}” repeats every week — scheduled jobs reflow around it.
              For a one-week exception, add a block instead.
            </Box>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  select size="small" label="From day" fullWidth value={recurringForm.dowStart}
                  onChange={(e) => setRecurringForm({ ...recurringForm, dowStart: Number(e.target.value) })}
                >
                  {DOW_LABELS.map((d, i) => <MenuItem key={d} value={i + 1}>{d}</MenuItem>)}
                </TextField>
                <TextField
                  size="small" type="time" label="From time" InputLabelProps={{ shrink: true }} sx={{ width: 140 }}
                  value={recurringForm.todStart}
                  onChange={(e) => setRecurringForm({ ...recurringForm, todStart: e.target.value })}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  select size="small" label="To day" fullWidth value={recurringForm.dowEnd}
                  onChange={(e) => setRecurringForm({ ...recurringForm, dowEnd: Number(e.target.value) })}
                >
                  {DOW_LABELS.map((d, i) => <MenuItem key={d} value={i + 1}>{d}</MenuItem>)}
                </TextField>
                <TextField
                  size="small" type="time" label="To time" InputLabelProps={{ shrink: true }} sx={{ width: 140 }}
                  value={recurringForm.todEnd}
                  onChange={(e) => setRecurringForm({ ...recurringForm, todEnd: e.target.value })}
                />
              </Box>
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setRecurringForm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" disableElevation sx={{ textTransform: 'none' }}
            onClick={() => { if (recurringForm) submitRecurring(recurringForm); }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* clear-future confirmation — admin/testing clean-up of a press's plan */}
      <Dialog open={!!clearConfirm} onClose={() => setClearConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
          Clear future plan on {clearConfirm}?
        </DialogTitle>
        <DialogContent>
          {(() => {
            const n = data.entries.filter(
              (e) => e.press === clearConfirm && e.status === 'planned',
            ).length;
            return (
              <Typography sx={{ fontSize: 14, color: TOKENS.ink2 }}>
                This deletes <b>all {n}</b> not-yet-started job{n === 1 ? '' : 's'} on{' '}
                <b>{clearConfirm}</b>. Running and completed jobs are left untouched, and the cleared
                jobs return to Ready-to-Run.
              </Typography>
            );
          })()}
          <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3, mt: 1.5 }}>
            Admin / testing clean-up — this can't be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained" disableElevation
            disabled={data.clearFuturePress.isPending}
            onClick={() => { if (clearConfirm) data.clearFuturePress.mutate({ press: clearConfirm }); setClearConfirm(null); }}
            sx={{ textTransform: 'none', bgcolor: TOKENS.bad, '&:hover': { bgcolor: '#b91c1c' } }}
          >
            Clear plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* group-removal confirmation — the planner has to see the job count */}
      <Dialog open={!!removeGroupConfirm} onClose={() => setRemoveGroupConfirm(null)} maxWidth="xs" fullWidth>
        {removeGroupConfirm && (() => {
          const held = removeGroupConfirm.kind === 'held';
          const fam = held ? removeGroupConfirm.fam : null;
          const entries = held ? [] : removeGroupConfirm.entries;
          const jobs = held ? fam!.jobs.map((j) => j.job) : entries.map((e) => e.job);
          const forme = (held ? fam!.forme : entries[0]?.forme) ?? '—';
          const cust = (held ? fam!.cust : entries[0]?.cust) ?? '—';
          const where = held ? 'Parking' : entries[0]?.press;
          const n = jobs.length;
          return (
            <>
              <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>
                Remove {n} job{n === 1 ? '' : 's'} from the schedule?
                <Typography sx={{ fontSize: 12, color: TOKENS.ink3, fontWeight: 400, mt: 0.25 }}>
                  {forme} · {cust} — on {where}
                </Typography>
              </DialogTitle>
              <DialogContent>
                <Typography sx={{ fontSize: 14, color: TOKENS.ink2 }}>
                  This takes the whole group off the plan. All <b>{n}</b> job{n === 1 ? '' : 's'}{' '}
                  return to the Ready-to-Run list at the bottom of the board.
                </Typography>
                <Box sx={{
                  mt: 1.5, p: 1, maxHeight: 148, overflowY: 'auto',
                  border: `1px solid ${TOKENS.line}`, borderRadius: '8px',
                  display: 'flex', flexWrap: 'wrap', gap: 0.5,
                }}>
                  {jobs.map((j) => (
                    <Box key={j} sx={{
                      fontFamily: TOKENS.monoFont, fontSize: 11.5, fontWeight: 600, color: TOKENS.ink2,
                      border: `1px solid ${TOKENS.line}`, borderRadius: '6px', px: 0.75, py: '2px',
                    }}>{j}</Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: 12.5, color: TOKENS.ink3, mt: 1.5 }}>
                  {held
                    ? 'Nothing is lost — the jobs can be scheduled again from Ready-to-Run.'
                    : 'Undo (Ctrl+Z) puts the group back on this press.'}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRemoveGroupConfirm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
                <Button
                  variant="contained" disableElevation
                  disabled={data.removeEntries.isPending || data.returnToBacklog.isPending}
                  onClick={confirmGroupRemoval}
                  sx={{ textTransform: 'none', bgcolor: TOKENS.bad, '&:hover': { bgcolor: '#b91c1c' } }}
                >
                  Remove {n} job{n === 1 ? '' : 's'}
                </Button>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

      {/* hover detail (suppressed while dragging) */}
      {!drag && <HoverPopover hover={hover} />}

      {/* drag ghost — backlog/parked family */}
      {drag?.tile && cursor && (
        <Box sx={{
          position: 'fixed', left: cursor.x + 14, top: cursor.y + 14, zIndex: 1600, pointerEvents: 'none',
          background: TOKENS.panel, border: `1px solid ${TOKENS.line}`, borderRadius: '8px', boxShadow: TOKENS.shadowMd,
          px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.75,
        }}>
          <Box sx={{ width: 4, height: 18, borderRadius: 99, background: drag.tile.seps[0]?.hex ?? TOKENS.accent }} />
          <Box sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 12 }}>{drag.tile.forme}</Box>
          <Box sx={{ fontSize: 11, color: TOKENS.ink2 }}>{drag.tile.cust} · {drag.tile.jobs.length} job{drag.tile.jobs.length === 1 ? '' : 's'}</Box>
        </Box>
      )}

      {/* drag ghost — moving a placed bar */}
      {drag?.entry && cursor && (
        <Box sx={{
          position: 'fixed', left: cursor.x + 14, top: cursor.y + 14, zIndex: 1600, pointerEvents: 'none',
          background: TOKENS.panel, border: `1px solid ${TOKENS.line}`, borderRadius: '8px', boxShadow: TOKENS.shadowMd,
          px: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.75,
        }}>
          <Box sx={{ width: 4, height: 18, borderRadius: 99, background: drag.entry.style.fill }} />
          <Box sx={{ fontFamily: TOKENS.monoFont, fontWeight: 700, fontSize: 12 }}>{drag.entry.job}</Box>
          <Box sx={{ fontSize: 11, color: TOKENS.ink2 }}>{drag.entry.cust}</Box>
        </Box>
      )}
    </Box>
  );
}
